/**
 * ReasonCell — an event's reason, with an "edited" badge, and an explanation on hover
 * for the auto-close sentinels (-1 autoclear, -2 auto finish), which have no reason
 * text a user would recognise.
 *
 * Replaces five local copies: the Alert Details and Executive report tables and the
 * area, resident and device Reporting tabs, which drew the edited marker three
 * different ways (docs/DUPLICATION_AUDIT.md, B17).
 *
 * @param {number|string} reasonId
 * @param {string} description — reason_code_description
 * @param {boolean} edited — the caller decides: reason_edits arrives as an array from
 *        the event report and as a count from the excess-alarm procs
 * @param {string} [fallback] — text when there is no description
 */

import { useState, useRef, useEffect } from 'react';
import './reason-cell.css';

const AUTO_CLOSE_REASON_MESSAGES = {
    '-1': 'This event was automatically closed by the system due to autoclear timeout.',
    '-2': 'This event was automatically closed by the system due to auto finish timeout.',
};

const POPOVER_WIDTH = 260;

const ReasonCell = ({ reasonId, description, edited = false, fallback = '—' }) => {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const message = AUTO_CLOSE_REASON_MESSAGES[String(reasonId)];
    const text = description || fallback;

    // Fixed-position popover: close it rather than leave it behind when anything scrolls.
    useEffect(() => {
        if (!open) return undefined;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const badge = edited ? (
        <span className="reason-cell__edited" title="This value has been edited">
            <i className="fe fe-edit-2" style={{ fontSize: '10px' }} />
        </span>
    ) : null;

    if (!message) {
        return (
            <span className="reason-cell">
                <span className="dp-cell-text">{text}</span>
                {badge}
            </span>
        );
    }

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const centerX = rect.left + rect.width / 2;
        const left = Math.max(8, Math.min(centerX - POPOVER_WIDTH / 2, window.innerWidth - POPOVER_WIDTH - 8));
        setStyle({
            position: 'fixed',
            zIndex: 9999,
            left: `${left}px`,
            top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            width: `${POPOVER_WIDTH}px`,
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span className="reason-cell">
            <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ position: 'relative', display: 'inline-block' }}>
                <span className="dp-cell-text has-popover">{text}</span>
                {open ? (
                    <span className="na-type-popover" style={style}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
                    </span>
                ) : null}
            </span>
            {badge}
        </span>
    );
};

export default ReasonCell;
